import { useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  HiOutlineBuildingOffice2,
  HiOutlineUsers,
  HiOutlineSquares2X2,
  HiOutlineLink,
  HiOutlineCpuChip,
  HiOutlineCreditCard,
} from "react-icons/hi2";
import {
  getDefaultRouteForRoles,
  getRolesForPath,
} from "../../constants/routeConfig";
import {
  SETTINGS_PAGES,
  getSettingsPage,
} from "../../constants/workspaceSettings";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import styles from "./SettingsLayout.module.css";

const ICONS = {
  company: HiOutlineBuildingOffice2,
  team: HiOutlineUsers,
  modules: HiOutlineSquares2X2,
  integrations: HiOutlineLink,
  automation: HiOutlineCpuChip,
  billing: HiOutlineCreditCard,
};

export default function SettingsLayout({ children }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const roles = useSelector((state) => state.user?.roles) || [];
  const companyName = useSelector((state) => state.user?.company?.name);
  const desktop = useMediaQuery("(min-width: 900px)");
  const contentRef = useRef(null);
  const generatedTitles = useRef(new WeakMap());
  const previousPath = useRef(pathname);
  const currentPage = getSettingsPage(pathname);
  const pages = SETTINGS_PAGES.filter(({ to }) =>
    getRolesForPath(to).some((role) => roles.includes(role)),
  );
  const visible = currentPage && pages.some(({ to }) => to === currentPage.to);

  useEffect(() => {
    if (visible && previousPath.current !== pathname) {
      // Announce the new page after navigation, never while a draft is blocked.
      contentRef.current?.closest("main")?.scrollTo?.({ top: 0 });
      contentRef.current?.focus({ preventScroll: true });
    }
    previousPath.current = pathname;
  }, [pathname, visible]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;

    const selector = "button[aria-label], a[aria-label], [role='button'][aria-label]";
    const updateTooltip = (element) => {
      if (!element.matches(selector) && !generatedTitles.current.has(element)) return;
      const previous = generatedTitles.current.get(element);
      if (element.title.trim() && element.title !== previous) return;
      const label = element.getAttribute("aria-label")?.trim() || "";
      element.title = label;
      generatedTitles.current.set(element, label);
    };
    const addTooltips = (scope) => {
      if (scope.matches?.(selector)) updateTooltip(scope);
      scope.querySelectorAll?.(selector).forEach(updateTooltip);
    };
    addTooltips(root);

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === "attributes") updateTooltip(record.target);
        else record.addedNodes.forEach((node) => addTooltips(node));
      });
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-label"] });
    return () => observer.disconnect();
  }, [visible, pathname]);

  if (!visible) return children;

  return (
    <div className={`${styles.layout} ${currentPage.id === "billing" ? styles.compact : ""}`}>
      <aside className={styles.menu}>
        <Link to={getDefaultRouteForRoles(roles)} className={styles.back}>
          {t("workspaceSettings.backToCRM")}
        </Link>
        {companyName && <p className={styles.companyName}>{companyName}</p>}
        {desktop ? (
          <nav aria-label={t("workspaceSettings.title")}>
            <ul className={styles.pages}>
              {pages.map((page) => {
                const Icon = ICONS[page.id];
                return (
                  <li key={page.to}>
                    <NavLink
                      to={page.to}
                      className={({ isActive }) =>
                        `${styles.link} ${isActive ? styles.active : ""}`
                      }
                    >
                      <Icon aria-hidden="true" focusable="false" />
                      <span>
                        {t(`workspaceSettings.navigation.${page.labelKey}`)}
                      </span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        ) : (
          <div className={styles.selector}>
            <label htmlFor="workspace-settings-page">
              {t("workspaceSettings.selectPage")}
            </label>
            <select
              id="workspace-settings-page"
              value={currentPage.to}
              onChange={(event) => navigate(event.target.value)}
            >
              {pages.map((page) => (
                <option key={page.to} value={page.to}>
                  {t(`workspaceSettings.navigation.${page.labelKey}`)}
                </option>
              ))}
            </select>
          </div>
        )}
      </aside>
      <div
        ref={contentRef}
        tabIndex={-1}
        data-settings-content
        role="region"
        aria-label={t(`workspaceSettings.navigation.${currentPage.labelKey}`)}
        className={styles.content}
      >
        {children}
      </div>
    </div>
  );
}
